import type { PriceListUsage } from "../api/user";
import { formatTariffDate } from "./userTariffFormat";

export type TariffTimelineStatus = "active" | "upcoming" | "expired";

const parseTariffDate = (value?: string): Date | null => {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (match) {
    const [, year, month, day] = match;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getTodayDate = (now = new Date()): Date =>
  new Date(now.getFullYear(), now.getMonth(), now.getDate());

export const getTariffTimelineStatus = (
  tariff: PriceListUsage,
  now = new Date()
): TariffTimelineStatus => {
  const start = parseTariffDate(tariff.startAt);
  const end = parseTariffDate(tariff.endAt);
  const today = getTodayDate(now);

  if (start && today < start) return "upcoming";
  if (end && today > end) return "expired";
  if (start || end) return "active";

  return "active";
};

export const sortTariffsByStartDate = (tariffs: PriceListUsage[]): PriceListUsage[] =>
  [...tariffs].sort((left, right) => {
    const leftStart = parseTariffDate(left.startAt)?.getTime() ?? 0;
    const rightStart = parseTariffDate(right.startAt)?.getTime() ?? 0;
    return leftStart - rightStart;
  });

export const getCurrentActiveTariff = (
  tariffs: PriceListUsage[],
  now = new Date()
): PriceListUsage | undefined =>
  sortTariffsByStartDate(tariffs).find(
    (tariff) => getTariffTimelineStatus(tariff, now) === "active"
  );

export const getUpcomingTariffs = (
  tariffs: PriceListUsage[],
  now = new Date()
): PriceListUsage[] =>
  sortTariffsByStartDate(tariffs).filter(
    (tariff) => getTariffTimelineStatus(tariff, now) === "upcoming"
  );

export const getVisibleTariffs = (
  tariffs: PriceListUsage[],
  now = new Date()
): PriceListUsage[] =>
  sortTariffsByStartDate(tariffs).filter(
    (tariff) => getTariffTimelineStatus(tariff, now) !== "expired"
  );

export const getProfileTariffTitle = (
  tariff: PriceListUsage,
  status: TariffTimelineStatus
): string => {
  const name = tariff.tariffName?.trim();
  if (status === "active") {
    return name || "Активный тариф";
  }

  return name || "Следующий тариф";
};

export const getProfileTariffBadge = (
  tariff: PriceListUsage,
  status: TariffTimelineStatus
): string | undefined => {
  if (status === "active") return "Активен";
  if (status === "upcoming") return `С ${formatTariffDate(tariff.startAt)}`;
  return undefined;
};
